import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import type {
  SeasonalityKeyword,
  TrendDecompositionTab,
  TrendKeyword,
  TrendOverview,
  TrendPeriod,
  TrendSeasonalityPayload,
} from '../models/trendSeasonality.types'

import type { ReportFilter } from '@/features/reports'

const EMPTY_OVERVIEW: TrendOverview = {
  total_monthly_observed_change: null,
  total_yearly_observed_change: null,
  total_monthly_observed_volume: null,
  total_yearly_observed_volume: null,
}

export const useTrendSeasonalityStore = defineStore('trend-seasonality', () => {
  const trends = shallowRef<TrendKeyword[]>([])
  const seasonality = shallowRef<SeasonalityKeyword[]>([])
  const overview = ref<TrendOverview>({ ...EMPTY_OVERVIEW })
  const filters = shallowRef<ReportFilter[]>([])
  const search = ref('')
  const activeTab = ref<TrendDecompositionTab>('volume')
  const period = ref<TrendPeriod>('monthly')
  const first = ref(0)
  const rows = ref(10)

  const activeKeywords = computed(() =>
    activeTab.value === 'seasonal' ? seasonality.value : trends.value,
  )
  const filteredKeywords = computed(() => {
    const needle = search.value.trim().toLocaleLowerCase()
    if (!needle) return activeKeywords.value
    return activeKeywords.value.filter((keyword) =>
      keyword.query.toLocaleLowerCase().includes(needle),
    )
  })
  const pagedKeywords = computed(() =>
    filteredKeywords.value.slice(first.value, first.value + rows.value),
  )

  function setPayload(payload: TrendSeasonalityPayload): void {
    trends.value = payload.trends
    seasonality.value = payload.seasonality
    overview.value = payload.overview
    first.value = 0
  }

  function setFilters(value: readonly ReportFilter[]): void {
    filters.value = value.map((filter) => ({
      ...filter,
      value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
    }))
    first.value = 0
  }

  function setPage(value: { first: number; rows: number }): void {
    first.value = value.first
    rows.value = value.rows
  }

  function clearData(): void {
    trends.value = []
    seasonality.value = []
    overview.value = { ...EMPTY_OVERVIEW }
    first.value = 0
  }

  function reset(): void {
    clearData()
    filters.value = []
    search.value = ''
    activeTab.value = 'volume'
    period.value = 'monthly'
    rows.value = 10
  }

  return {
    trends,
    seasonality,
    overview,
    filters,
    search,
    activeTab,
    period,
    first,
    rows,
    activeKeywords,
    filteredKeywords,
    pagedKeywords,
    setPayload,
    setFilters,
    setPage,
    clearData,
    reset,
  }
})

export type TrendSeasonalityStore = ReturnType<typeof useTrendSeasonalityStore>
